"use client";

import { useEffect, useRef } from "react";
import { trackPurchaseSuccess } from "@/services/analytics";
import { parsePhoneNumber } from "libphonenumber-js";
import config from "@/config/app";
import { useI18n } from "@/i18n/I18nProvider";

/** Love map add-on purchased with the letter, as shown after payment. */
export interface SuccessLoveMap {
  forecastYear: number;
  feeCents: number;
  deliveryTargetHours: number;
}

interface PaymentSuccessScreenProps {
  requestId: string;
  whatsappNumber: string;
  querentName: string;
  valueBRL?: number;
  currency?: string; // Moeda do pagamento (BRL, USD, etc)
  email?: string; // email opcional informado no step 1
  deliveryOption?: "email" | "whatsapp" | "email_whatsapp";
  priorityDelivery?: boolean;
  priorityDeliveryFeeCents?: number;
  deliveryTargetMinutes?: number;
  loveMap?: SuccessLoveMap | null;
  onBackToStep1: () => void;
}

export function PaymentSuccessScreen({
  requestId,
  whatsappNumber,
  querentName,
  valueBRL,
  currency,
  email,
  deliveryOption = whatsappNumber ? "email_whatsapp" : "email",
  priorityDelivery = false,
  priorityDeliveryFeeCents = 0,
  deliveryTargetMinutes,
  loveMap,
  onBackToStep1: onBackToStep1,
}: PaymentSuccessScreenProps) {
  const { t } = useI18n();
  // Formatação amigável do número de telefone para exibição
  const formatPhoneForDisplay = (phone: string): string => {
    try {
      const lang = config.i18n.language;
      const countryConfig: Record<
        string,
        { country: "BR" | "US" | "PT"; callingCode: string }
      > = {
        "pt-BR": { country: "BR", callingCode: "55" },
        "en-US": { country: "US", callingCode: "1" },
        "pt-PT": { country: "PT", callingCode: "351" },
      };

      const selected = countryConfig[lang];
      let phoneToFormat = phone;

      // Se o número não tem +, adiciona conforme o idioma configurado
      if (!phone.startsWith("+")) {
        if (selected) {
          phoneToFormat = phone.startsWith(selected.callingCode)
            ? `+${phone}`
            : `+${selected.callingCode}${phone}`;
        } else {
          phoneToFormat = `+${phone}`;
        }
      }

      const parsed = selected
        ? parsePhoneNumber(phoneToFormat, selected.country)
        : parsePhoneNumber(phoneToFormat);
      if (parsed && parsed.isValid()) {
        return parsed.formatNational();
      }
    } catch (error) {
      console.warn("Erro ao formatar telefone para exibição:", error);
    }
    // Fallback: retorna o número original
    return phone;
  };

  const displayPhoneNumber = formatPhoneForDisplay(whatsappNumber);

  // Scroll to top when component mounts
  const purchaseFiredRef = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!requestId || purchaseFiredRef.current) return;
    purchaseFiredRef.current = true;
    try {
      const getCookie = (name: string) =>
        typeof document === "undefined"
          ? undefined
          : document.cookie.match(
              new RegExp("(?:^|; )" + name + "=([^;]*)"),
            )?.[1];
      // Narrow window type for fbq without using 'any'
      type FbqWindow = Window & { fbq?: (...args: unknown[]) => void };
      const hasFbq = typeof (window as FbqWindow).fbq === "function";
      const fbp = getCookie("_fbp");
      const fbc = getCookie("_fbc");
      const debug = {
        page: "PaymentSuccessScreen",
        requestId,
        valueBRL,
        pixelEnabled: config.analytics?.pixelEnabled,
        capiEnabled: config.analytics?.capiEnabled,
        apiBaseUrl: config.api.baseUrl,
        hasFbq,
        hasFbpCookie: Boolean(fbp),
        hasFbcCookie: Boolean(fbc),
        href: typeof window !== "undefined" ? window.location.href : undefined,
      };
      console.info("[Analytics] Purchase attempt", debug);

      // Try to include payer email captured from the Mercado Pago brick if user didn't fill the form email
      const payerEmail =
        typeof window !== "undefined"
          ? sessionStorage.getItem("mp_payer_email") || email || undefined
          : email || undefined;
      Promise.resolve(
        trackPurchaseSuccess({
          orderId: requestId,
          valueBRL,
          currency, // Pass currency (BRL, USD, etc)
          // Envia o telefone para melhorar a correspondência (Meta recomenda ph)
          phone: whatsappNumber,
          // Include email when available to improve match
          email: payerEmail,
          alsoPixel: true,
        }),
      )
        .then((res) => {
          console.info("[Analytics] Purchase tracking completed", {
            ok: true,
            res,
          });
        })
        .catch((err) => {
          console.warn("[Analytics] Purchase tracking failed", {
            ok: false,
            err,
          });
        });
    } catch (err) {
      console.warn("[Analytics] Failed before sending Purchase", err);
    }
  }, [requestId, valueBRL, currency, email, whatsappNumber]);

  const supportContact = config.support;
  const isWhatsappSupport = supportContact.channel === "whatsapp";
  const supportLabel = isWhatsappSupport
    ? `WhatsApp ${supportContact.whatsappDisplay}`
    : supportContact.instagramHandle;
  const supportUrl = isWhatsappSupport
    ? supportContact.whatsappUrl
    : `https://instagram.com/${supportContact.instagramHandle.replace(/^@/, "")}`;
  const supportFollowup = isWhatsappSupport
    ? t(
        "success.timeline.3.followup.whatsapp",
        "Se não receber dentro desse prazo, fale com a gente pelo WhatsApp",
      )
    : t(
        "success.timeline.3.followup",
        "Se não receber dentro desse prazo, fale com a gente pelo Instagram",
      );

  const deliveryMinutes = deliveryTargetMinutes || 120;
  const deliveryTime =
    deliveryMinutes >= 60 && deliveryMinutes % 60 === 0
      ? t("success.delivery.hours", "{hours} horas").replace(
          "{hours}",
          String(deliveryMinutes / 60),
        )
      : t("success.delivery.minutes", "{minutes} minutos").replace(
          "{minutes}",
          String(deliveryMinutes),
        );
  const formattedPriorityFee = new Intl.NumberFormat(
    config.i18n.language,
    { style: "currency", currency: currency || "BRL" },
  ).format(priorityDeliveryFeeCents / 100);
  const loveMapHours = loveMap?.deliveryTargetHours ?? 0;
  const loveMapDeliveryTime =
    loveMapHours >= 24 && loveMapHours % 24 === 0
      ? t("success.delivery.days", "{days} dias").replace(
          "{days}",
          String(loveMapHours / 24),
        )
      : t("success.delivery.hours", "{hours} horas").replace(
          "{hours}",
          String(loveMapHours),
        );
  const formattedLoveMapFee = new Intl.NumberFormat(
    config.i18n.language,
    { style: "currency", currency: currency || "BRL" },
  ).format((loveMap?.feeCents ?? 0) / 100);

  const pageBackgroundStyle = { background: "#ffffff" };
  return (
    <div className="order-flow-font min-h-screen py-3" style={pageBackgroundStyle}>
      <div className="container mx-auto px-4 py-3 max-w-4xl">
        {/* Container principal alinhado ao tema atual do form */}
        <div
          className="rounded-2xl p-4 md:p-6 relative shadow-xl mb-4"
          style={{
            backgroundColor: "#fdf2f2",
            border: "2px solid #800000",
            boxShadow:
              "0 8px 25px rgba(128,0,0,0.3), 0 0 0 1px rgba(128,0,0,0.1)",
          }}
        >
          {/* Header do sucesso */}
          <div className="text-center mb-6">
            <div
              className="text-xl md:text-2xl font-bold tracking-wider font-title mb-1"
              style={{
                color: "#800000",
                textShadow: "0 2px 6px rgba(0,0,0,0.25)",
              }}
            >
              {t("success.title", "PAGAMENTO CONFIRMADO!")}
            </div>
            <p className="text-base font-subtitle" style={{ color: "#4a2c2c" }}>
              {t("success.subtitle", "Sua jornada espiritual começou")}
            </p>
          </div>

          {/* Conteúdo principal */}
          <div className="space-y-4">
            {/* Mensagem principal */}
            <div className="text-center space-y-2">
              <h2 className="text-lg font-bold text-tarot-marrom-escuro font-title">
                {t("success.hello", "Olá, {name}! ").replace(
                  "{name}",
                  querentName,
                )}
              </h2>

              <p className="text-sm text-tarot-marrom font-subtitle leading-relaxed">
                {t(
                  "success.message_prefix",
                  "Seu pagamento foi processado com",
                )}{" "}
                <strong>{t("success.message_success", "sucesso")}</strong>{" "}
                {t(
                  "success.message_suffix",
                  "e sua solicitação de carta canalizada foi criada!",
                )}
              </p>
            </div>

            {/* Informações detalhadas */}
            <div className="bg-white/50 rounded-lg p-4 border border-[rgba(232,115,115,0.2)]">
              <h3 className="text-lg font-bold text-tarot-marrom-escuro font-title mb-3 text-center">
                📋 {t("success.details.title", "Detalhes da sua solicitação")}
              </h3>

              <div className="space-y-2">
                <div className="flex items-center justify-between py-1">
                  <span className="text-tarot-marrom font-subtitle text-sm">
                    🔮 {t("success.details.id", "ID da Solicitação:")}
                  </span>
                  <span className="font-bold text-tarot-carmim font-title text-sm">
                    #9C{requestId}
                  </span>
                </div>

                {email && deliveryOption !== "whatsapp" && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-tarot-marrom font-subtitle text-sm">
                      📧 {t("success.details.email", "Email de entrega:")}
                    </span>
                    <span className="font-bold text-tarot-marrom-escuro font-subtitle text-sm">
                      {email}
                    </span>
                  </div>
                )}

                {whatsappNumber &&
                  whatsappNumber.trim() !== "" &&
                  whatsappNumber !== "+" && (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-tarot-marrom font-subtitle text-sm">
                        📱{" "}
                        {t("success.details.whatsapp", "WhatsApp de entrega:")}
                      </span>
                      <span className="font-bold text-tarot-marrom-escuro font-subtitle text-sm">
                        {displayPhoneNumber}
                      </span>
                    </div>
                  )}

                <div className="flex items-center justify-between py-1">
                  <span className="text-tarot-marrom font-subtitle text-sm">
                    ⏰ {t("success.details.deadline", "Prazo de entrega:")}
                  </span>
                  <span className="font-bold text-green-700 font-subtitle text-sm">
                    {t("success.details.deadline_time", "Até {time}").replace(
                      "{time}",
                      deliveryTime,
                    )}
                  </span>
                </div>

                {priorityDelivery && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
                    <p className="font-bold text-tarot-marrom-escuro font-title">
                      {t(
                        "success.priority.title",
                        "⚡ Entrega prioritária adquirida",
                      )}
                    </p>
                    <p className="mt-1 text-tarot-marrom font-subtitle">
                      {t(
                        "success.priority.description",
                        "Seu pedido terá prioridade e será entregue em até {time} após a confirmação do pagamento.",
                      ).replace("{time}", deliveryTime)}
                    </p>
                    {priorityDeliveryFeeCents > 0 && (
                      <p className="mt-1 font-bold text-emerald-800 font-subtitle">
                        {t("success.priority.fee", "Adicional contratado: {price}").replace(
                          "{price}",
                          formattedPriorityFee,
                        )}
                      </p>
                    )}
                  </div>
                )}

                {loveMap && (
                  <div className="rounded-md border border-[rgba(232,115,115,0.45)] bg-rose-50 px-3 py-2 text-sm">
                    <p className="font-bold text-tarot-marrom-escuro font-title">
                      {t(
                        "success.love_map.title",
                        "💞 Previsão astrológica {year} adquirida",
                      ).replace("{year}", String(loveMap.forecastYear))}
                    </p>
                    <p className="mt-1 text-tarot-marrom font-subtitle">
                      {t(
                        "success.love_map.description",
                        "Sua previsão para vocês dois em {year} será entregue separadamente da carta, em até {time} após a confirmação do pagamento.",
                      )
                        .replace("{year}", String(loveMap.forecastYear))
                        .replace("{time}", loveMapDeliveryTime)}
                    </p>
                    {loveMap.feeCents > 0 && (
                      <p className="mt-1 font-bold text-emerald-800 font-subtitle">
                        {t("success.priority.fee", "Adicional contratado: {price}").replace(
                          "{price}",
                          formattedLoveMapFee,
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Timeline do que acontece agora */}
            <div className="bg-white/50 rounded-lg p-4 border border-[rgba(232,115,115,0.2)]">
              <h3 className="text-lg font-bold text-tarot-marrom-escuro font-title mb-3 text-center">
                🌟 {t("success.timeline.title", "O que acontece agora?")}
              </h3>

              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-tarot-carmim rounded-full flex items-center justify-center text-white font-bold text-xs">
                    1
                  </div>
                  <div>
                    <h4 className="font-bold text-tarot-marrom-escuro font-title text-sm">
                      {t("success.timeline.1.title", "Canalização Espiritual")}
                    </h4>
                    <p className="text-tarot-marrom font-subtitle text-xs">
                      {t(
                        "success.timeline.1.desc",
                        "Nossa taróloga iniciará o processo de canalização da sua pessoa especial.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-tarot-carmim rounded-full flex items-center justify-center text-white font-bold text-xs">
                    2
                  </div>
                  <div>
                    <h4 className="font-bold text-tarot-marrom-escuro font-title text-sm">
                      {t(
                        "success.timeline.2.title",
                        "Criação da Carta Personalizada",
                      )}
                    </h4>
                    <p className="text-tarot-marrom font-subtitle text-xs">
                      {t(
                        "success.timeline.2.desc",
                        "Sua carta será criada com base nas informações fornecidas e nas revelações da canalização espiritual.",
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-tarot-carmim rounded-full flex items-center justify-center text-white font-bold text-xs">
                    3
                  </div>
                  <div>
                    <h4 className="font-bold text-tarot-marrom-escuro font-title text-sm">
                      {t("success.timeline.3.title", "Entrega")}
                    </h4>
                    <p className="text-tarot-marrom font-subtitle text-xs">
                      {t(
                        deliveryOption === "whatsapp"
                          ? "success.timeline.3.whatsapp_desc"
                          : deliveryOption === "email_whatsapp"
                            ? "success.timeline.3.both_desc"
                            : "success.timeline.3.email_desc",
                        deliveryOption === "whatsapp"
                          ? "Você receberá sua carta pelo WhatsApp em até {time}."
                          : deliveryOption === "email_whatsapp"
                            ? "Você receberá sua carta por email e WhatsApp em até {time}."
                            : "Você receberá sua carta por email em até {time}.",
                      ).replace("{time}", deliveryTime)}
                    </p>
                    <p className="text-tarot-marrom font-subtitle text-xs mt-1">
                      {supportFollowup}{" "}
                      <a
                        href={supportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-tarot-carmim"
                      >
                        {supportLabel}
                      </a>{" "}
                      {t(
                        "success.timeline.3.followup_end",
                        "e envie um print deste comprovante.",
                      )}
                    </p>
                    {deliveryOption !== "whatsapp" && (
                      <p className="text-orange-700 font-subtitle text-xs mt-2 bg-yellow-50 p-2 rounded border border-yellow-300">
                        ⚠️{" "}
                        {t(
                          "success.timeline.3.spam_warning",
                          "Importante: Verifique também sua caixa de SPAM ou lixo eletrônico, pois alguns emails podem ser direcionados para lá.",
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Botão de ação */}
            <div className="text-center pt-2">
              <button
                onClick={onBackToStep1}
                className="px-6 py-3 text-base rounded-lg font-bold shadow-lg font-title"
                style={{
                  background:
                    "linear-gradient(to bottom, #059669, #047857, #065f46)",
                  border: "3px solid #34d399",
                  color: "#ffffff",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
                }}
              >
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-lg filter drop-shadow-lg">
                    ✨
                  </span>
                  <span>
                    {t("success.cta.new_request", "Fazer Nova Solicitação")}
                  </span>
                  <span className="text-lg filter drop-shadow-lg">
                    ✨
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white/50 p-2 text-center border border-[rgba(232,115,115,0.2)] rounded-lg mt-3">
            <p className="text-tarot-marrom font-subtitle text-xs">
              {t(
                "success.footer.thanks",
                "Gratidão por confiar em nossos serviços espirituais 🙏",
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
